import React, {
  useCallback,
  forwardRef,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay } from '@fortawesome/pro-solid-svg-icons';
import css from './date-filter.module.css';
import { useQuery } from '../../../../hooks/use-query';
import { isValidDateSting } from '../../../../services/utils';
import { DefaultFormats } from '../../../../enums/default-formats';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import cn from 'classnames';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import calendarImage from '../../../../../src/assets/icons/calendar.svg';

interface CustomInputProps {
  customIcon?: IconProp;
}

export enum DateFields {
  from = 'from',
  to = 'to',
  date = 'date',
}

export enum DateRanges {
  today = 'today',
  thisMonth = 'this_month',
  lastMonth = 'last_month',
  thisYear = 'this_year',
}

//Custom input
const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ customIcon, ...props }, ref) => {
    return (
      <label>
        <input {...props} autoComplete="off" />
        <FontAwesomeIcon icon={customIcon ? customIcon : faCalendarDay} />
      </label>
    );
  }
);
const ButtonCustomInput = forwardRef<HTMLButtonElement, any>((props, ref) => (
  <label {...props}>
    <img src={calendarImage} alt="" />
  </label>
));

export default function DateFilter({
  name,
  fields,
  showDateControls = false,
  className = 'date-filter',
  placeholder = '',
  isClearable = true,
  customIcon,
  inline = false,
  defaultValue = null,
  disableFutureDates = false,
  debounceTime = 0,
}: {
  name: string;
  fields: DateFields[];
  showDateControls?: boolean;
  className?: string;
  placeholder?: string;
  isClearable?: boolean;
  customIcon?: IconProp;
  inline?: boolean;
  defaultValue?: Date;
  disableFutureDates?: boolean;
  debounceTime?: number;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [params] = useQuery();

  const refCustomInput = useRef();

  const debouncedHandleDateChange = useMemo(
    () =>
      AwesomeDebouncePromise((field, date) => {
        const search = new URLSearchParams(history.location.search);

        const filterName = name.concat('-', field);

        if (!date || !isValidDateSting(date.toString())) {
          search.delete(field);
          search.delete('page');

          sessionStorage.removeItem(filterName);

          history.push({ ...history.location, search: search.toString() });
        } else {
          search.set(
            field,
            date ? date.toLocaleDateString(DefaultFormats.DATE_FILTER) : null
          );
        }

        sessionStorage.setItem(
          filterName,
          date ? date.toLocaleDateString(DefaultFormats.DATE_FILTER) : null
        );
        history.push({ ...history.location, search: search.toString() });
      }, debounceTime),
    [history, name, debounceTime]
  );

  const onChange = useCallback(
    (field, date) => {
      debouncedHandleDateChange(field, date);
    },
    [debouncedHandleDateChange]
  );

  const onSelect = useCallback(
    (field: DateFields, date: string) => {
      const search = new URLSearchParams(history.location.search);
      search.set(field, date);
      search.delete('page');

      sessionStorage.setItem(name.concat('-', field), date);

      history.push({ ...history.location, search: search.toString() });
    },
    [history, name]
  );

  // function for date controls
  const setDateRange = useCallback(
    (dateRange: string) => {
      const today = new Date();

      let startDate;
      let endDate;

      const range = dateRange as keyof typeof DateRanges as DateRanges;

      switch (range) {
        case DateRanges.today:
          startDate = today.toLocaleDateString(DefaultFormats.DATE_FILTER);
          endDate = today.toLocaleDateString(DefaultFormats.DATE_FILTER);
          break;
        case DateRanges.thisMonth:
          startDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
          ).toLocaleDateString(DefaultFormats.DATE_FILTER);
          endDate = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          ).toLocaleDateString(DefaultFormats.DATE_FILTER);
          break;
        case DateRanges.lastMonth:
          startDate = new Date(
            today.getFullYear(),
            today.getMonth() - 1,
            1
          ).toLocaleDateString(DefaultFormats.DATE_FILTER);
          endDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            0
          ).toLocaleDateString(DefaultFormats.DATE_FILTER);
          break;
        default:
          startDate = new Date(today.getFullYear(), 0, 1).toLocaleDateString(
            DefaultFormats.DATE_FILTER
          );
          endDate = new Date(today.getFullYear(), 11, 31).toLocaleDateString(
            DefaultFormats.DATE_FILTER
          );
          break;
      }

      const search = new URLSearchParams(history.location.search);
      search.set(DateFields.from, startDate);
      search.set(DateFields.to, endDate);
      search.delete('page');

      sessionStorage.setItem(name.concat('-from'), startDate);
      sessionStorage.setItem(name.concat('-to'), endDate);

      history.replace({ ...history.location, search: search.toString() });
    },
    [history, name]
  );

  useEffect(() => {
    fields.forEach((field): void => {
      const search = new URLSearchParams(history.location.search);

      const initialSearch = search;

      if (isValidDateSting(params[field]?.toString())) {
        search.delete(field);
      }

      if (search.toString() !== initialSearch.toString()) {
        history.push({ ...history.location, search: search.toString() });
      }
    });
  }, [fields, history, params]);

  const minDate = useCallback(
    (field: DateFields) => {
      return field === 'to' && Date.parse(params['from']?.toString())
        ? new Date(params['from'].toString())
        : null;
    },
    [params]
  );
  const maxDate = useCallback(
    (field: DateFields) => {
      return field === 'from' && Date.parse(params['to']?.toString())
        ? new Date(params['to'].toString())
        : null;
    },
    [params]
  );
  return (
    <form className={css[className]}>
      {fields.map((field, i) => {
        const selectedDate = Date.parse(params[field]?.toString())
          ? new Date(params[field].toString())
          : defaultValue ?? null;
        return (
          <div className={css['date']} key={field}>
            {i > 0 && <div className={css['delimiter']}>&mdash;</div>}

            <div
              className={cn('react-datepicker-container', { inline: inline })}
            >
              <DatePicker
                name={field}
                minDate={minDate(field)}
                maxDate={disableFutureDates ? new Date() : maxDate(field)}
                locale="lv-LV"
                dateFormat="dd.MM.yyyy"
                selected={selectedDate}
                onChange={(date) => onChange(field, date)}
                onSelect={(date) =>
                  onSelect(
                    field,
                    date.toLocaleDateString(DefaultFormats.DATE_FILTER)
                  )
                }
                customInput={
                  inline ? (
                    <ButtonCustomInput ref={refCustomInput} />
                  ) : (
                    <CustomInput customIcon={customIcon} ref={refCustomInput} />
                  )
                }
                isClearable={isClearable}
                placeholderText={placeholder}
              >
                {showDateControls === true && (
                  <div className={css['date-controls']}>
                    <div>
                      <span
                        onClick={() => setDateRange(DateRanges.today)}
                        className={css['today']}
                      >
                        {t('today')} &nbsp; &gt;
                      </span>
                      <span onClick={() => setDateRange(DateRanges.thisMonth)}>
                        {t('current_month')} &nbsp; &gt;
                      </span>
                    </div>
                    <div>
                      <span
                        onClick={() => setDateRange(DateRanges.lastMonth)}
                        className={css['last-month']}
                      >
                        {t('previous_month_short')} &nbsp; &gt;
                      </span>
                      <span onClick={() => setDateRange(DateRanges.thisYear)}>
                        {t('current_year')} &nbsp; &gt;
                      </span>
                    </div>
                  </div>
                )}
              </DatePicker>
            </div>
          </div>
        );
      })}
    </form>
  );
}
